import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase-init';
import { PDFDocument } from 'pdf-lib';
import { useNotification } from '../../components/NotificationModal';

const BROWN = '#8B4513';
const TEAL  = '#1a5c52';

/* ── helpers ─────────────────────────────────────────────────────────── */
function fmtMoney(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return v || '';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Fill the Contract PDF (mirrors ContractBuilderPage exactly) ─────── */
async function fillContractPDF(fields, repSigDataUrl, customerSigDataUrl) {
  const response = await fetch('/assets/contract/Contract.pdf');
  const pdfBytes = await response.arrayBuffer();
  const pdfDoc   = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  const form     = pdfDoc.getForm();

  function setTextField(name, value) {
    try { form.getTextField(name).setText(value || ''); } catch(e) { /* skip */ }
  }
  function setDropdown(name, value) {
    try {
      const f = form.getDropdown(name);
      if (value && f.getOptions().includes(value)) f.select(value);
    } catch(e) { /* skip */ }
  }
  function setCheckbox(name, checked) {
    try {
      const f = form.getCheckBox(name);
      if (checked) f.check(); else f.uncheck();
    } catch(e) { /* skip */ }
  }

  setTextField('contract number',                       fields.contractNumber);
  setTextField('Purchaser',                             fields.purchaserName);
  setTextField('Address',                               fields.address);
  setTextField('CitystZip 1',                           fields.cityStateZip);
  setTextField('Email',                                 fields.email);
  setTextField('Home Phone',                            fields.homePhone);
  setTextField('Cell Phone',                            fields.cellPhone);
  setTextField('Work Phone',                            fields.workPhone);
  setTextField('Fax Number',                            fields.faxNumber);
  setTextField('Project Description',                   fields.projectDescription);
  setTextField('alumn wrap Color',                      fields.wrapColor);
  setTextField('1 Cash Price including sales tax',      fmtMoney(fields.cashPrice));
  setTextField('2 Cash Down Payment',                   fmtMoney(fields.downPayment));
  setTextField('3 UNPAID BALANCE  DUE UPON COMPLETION', fmtMoney(fields.unpaidBalance));
  setTextField('date',                                  fields.sigDay);
  setTextField('20',                                    fields.sigYear);

  setDropdown('trash',                  fields.trash);
  setDropdown('apprx installtion date', fields.installMonth);
  setDropdown('month of signing',       fields.sigMonth);

  setCheckbox('Low E argon gas', fields.cbLowE);
  setCheckbox('gas',             fields.cbScreen);
  setCheckbox('outside trim',    fields.cbOutsideTrim);
  setCheckbox('brickmold',       fields.cbBrickmold);
  setCheckbox('1x4',             fields.cbOneByFour);

  // Embed rep signature
  if (repSigDataUrl) {
    try {
      const base64   = repSigDataUrl.split(',')[1];
      const sigBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const sigImage = await pdfDoc.embedPng(sigBytes);
      const sigField = form.getField('Represenative signature');
      const widgets  = sigField.acroField.getWidgets();
      if (widgets.length > 0) {
        const rect = widgets[0].getRectangle();
        pdfDoc.getPage(0).drawImage(sigImage, { x: rect.x, y: rect.y, width: rect.width, height: rect.height });
      }
    } catch(e) { console.warn('Rep sig:', e.message); }
  }

  // Embed customer signature
  if (customerSigDataUrl) {
    try {
      const base64   = customerSigDataUrl.split(',')[1];
      const sigBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const sigImage = await pdfDoc.embedPng(sigBytes);

      // Try known purchaser field names
      let placed = false;
      for (const name of ['Purchaser signature', 'purchaser signature', 'Purchaser Signature', 'PURCHASER']) {
        try {
          const sigField = form.getField(name);
          const widgets  = sigField.acroField.getWidgets();
          if (widgets.length > 0) {
            const rect = widgets[0].getRectangle();
            pdfDoc.getPage(0).drawImage(sigImage, { x: rect.x, y: rect.y, width: rect.width, height: rect.height });
            placed = true;
            break;
          }
        } catch(e) { /* try next */ }
      }
      // Fallback: draw near purchaser X line
      if (!placed) {
        pdfDoc.getPage(0).drawImage(sigImage, { x: 52, y: 58, width: 200, height: 40 });
      }
    } catch(e) { console.warn('Customer sig:', e.message); }
  }

  form.flatten();
  return await pdfDoc.save(); // returns Uint8Array
}

/* ── Render PDF pages to canvas images using pdfjsLib ────────────────── */
async function renderPdfToImages(pdfData, scale = 2.5) {
  // Dynamically load pdf.js from CDN
  if (!window.pdfjsLib) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }

  const pdf = await window.pdfjsLib.getDocument({ data: pdfData }).promise;
  const images = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page     = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas   = document.createElement('canvas');
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    const ctx      = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    images.push({ dataUrl: canvas.toDataURL('image/png'), width: viewport.width, height: viewport.height });
  }
  return images;
}

/* ── Build fields from Firestore contract ────────────────────────────── */
function buildFieldsFromContract(contract) {
  const cust = contract.customer || {};
  const cx   = contract.checkboxes || {};
  return {
    contractNumber:     contract.contractNumber || '',
    purchaserName:      cust.name || '',
    address:            cust.address || '',
    cityStateZip:       cust.cityStateZip || cust.city || '',
    email:              cust.email || '',
    homePhone:          cust.homePhone || cust.phone || '',
    cellPhone:          cust.cellPhone || cust.cell || '',
    workPhone:          cust.workPhone || '',
    faxNumber:          cust.faxNumber || '',
    projectDescription: contract.projectDescription || '',
    installMonth:       contract.installMonth || contract.installDate || '',
    wrapColor:          contract.wrapColor || contract.aluminumWrapColor || '',
    trash:              contract.trash || '',
    cashPrice:          contract.cashPrice || '',
    downPayment:        contract.downPayment || '',
    unpaidBalance:      contract.unpaidBalance || '',
    sigDay:             contract.sigDay || '',
    sigMonth:           contract.sigMonth || '',
    sigYear:            contract.sigYear || '',
    cbLowE:             cx.lowE || cx.lowEArgonGas || false,
    cbScreen:           cx.screen || false,
    cbOutsideTrim:      cx.outsideTrim || false,
    cbBrickmold:        cx.brickmold || false,
    cbOneByFour:        cx.oneByFour || false,
  };
}

/* ── Reusable PDF page renderer component ────────────────────────────── */
function PdfPages({ images }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
      {images.map((img, i) => (
        <img
          key={i}
          src={img.dataUrl}
          alt={`Contract page ${i + 1}`}
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '6px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            display: 'block',
          }}
        />
      ))}
      {images.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#aaa' }}>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ width: '28px', height: '28px', border: `3px solid #e0d5cc`, borderTop: `3px solid ${BROWN}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Rendering contract…
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════ */
export default function ContractPublicView() {
  const { token }      = useParams();
  const [estimate,     setEstimate]     = useState(null);
  const notify = useNotification();
  const [estimateId,   setEstimateId]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [notFound,     setNotFound]     = useState(false);
  const [stage,        setStage]        = useState('view');
  const [submitting,   setSubmitting]   = useState(false);
  const [pageImages,   setPageImages]   = useState([]);
  const [pdfBytes,     setPdfBytes]     = useState(null);
  const [sigEmpty,     setSigEmpty]     = useState(true);
  const [signedBytes,  setSignedBytes]  = useState(null);
  const [signedImages, setSignedImages] = useState([]);
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef(null);

  /* ── Load estimate → fill PDF → render pages ─────────────────────── */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const q    = query(collection(db, 'estimates'), where('contract.contractToken', '==', token));
        const snap = await getDocs(q);
        if (snap.empty) { setNotFound(true); setLoading(false); return; }

        const d   = snap.docs[0];
        const est = d.data();
        if (cancelled) return;
        setEstimate(est);
        setEstimateId(d.id);

        const contract      = est.contract || {};
        const alreadySigned = contract.contractStatus === 'signed';
        const fields        = buildFieldsFromContract(contract);
        const repSig        = contract.repSignature || null;
        const custSig       = alreadySigned ? contract.signedBy?.signatureData : null;

        const bytes = await fillContractPDF(fields, repSig, custSig);
        if (cancelled) return;
        setPdfBytes(bytes);

        const imgs = await renderPdfToImages(bytes);
        if (cancelled) return;
        setPageImages(imgs);
        if (alreadySigned) { setSignedBytes(bytes); setSignedImages(imgs); }
      } catch(e) {
        console.error(e);
        if (!cancelled) setNotFound(true);
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  /* ── Canvas signature pad ────────────────────────────────────────── */
  useEffect(() => {
    if (stage !== 'sign' || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const dpr    = window.devicePixelRatio || 1;
    const rect   = canvas.getBoundingClientRect();
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);
    setSigEmpty(true);
  }, [stage]);

  const getPos = useCallback((e, canvas) => {
    const r   = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - r.left, y: src.clientY - r.top };
  }, []);

  function sigStart(e) { e.preventDefault(); drawingRef.current = true; lastPosRef.current = getPos(e, canvasRef.current); }
  function sigMove(e) {
    e.preventDefault();
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e, canvasRef.current);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.stroke();
    lastPosRef.current = pos;
    setSigEmpty(false);
  }
  function sigEnd() { drawingRef.current = false; }
  function sigClear() {
    const c = canvasRef.current, ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    setSigEmpty(true);
  }

  /* ── Submit signature (UPDATED — sends signed PDF to cloud function) ── */
  async function handleSign() {
    if (sigEmpty) { notify.warning('Signature required', 'Please sign in the box above before submitting.'); return; }
    setSubmitting(true);
    try {
      const signatureData = canvasRef.current.toDataURL('image/png');
      const signedDate    = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      const contract = estimate?.contract || {};
      const fields   = buildFieldsFromContract(contract);
      const repSig   = contract.repSignature || null;
      const bytes    = await fillContractPDF(fields, repSig, signatureData);
      setSignedBytes(bytes);

      const imgs = await renderPdfToImages(bytes);
      setSignedImages(imgs);

      // Convert signed PDF Uint8Array to base64 for cloud function
      let signedPdfBase64 = null;
      try {
        const chunkSize = 8192;
        let binaryStr = '';
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binaryStr += String.fromCharCode.apply(null, bytes.slice(i, i + chunkSize));
        }
        signedPdfBase64 = btoa(binaryStr);
      } catch (e) {
        console.warn('Could not convert PDF to base64:', e.message);
      }

      await updateDoc(doc(db, 'estimates', estimateId), {
        'contract.contractStatus': 'signed',
        'contract.signedBy': { signatureData, date: signedDate, timestamp: new Date().toISOString() },
        status: 'contract_signed',
      });

      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const app = (await import('../../services/firebase-init')).default;
        const fns = getFunctions(app);
        await httpsCallable(fns, 'onContractSigned')({
          estimateId,
          signatureData,
          signedDate,
          signedPdfBase64,
        });
      } catch(e) { console.warn('Notification failed:', e.message); }

      setStage('done');
    } catch(e) { console.error(e); notify.error('Could not submit', 'Something went wrong. Please try again or call (605) 432-1834.', e?.message); }
    setSubmitting(false);
  }

  /* ── Download PDF ────────────────────────────────────────────────── */
  function handleDownload() {
    const bytes = signedBytes || pdfBytes;
    if (!bytes) return;
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Contract_${estimate?.contract?.customer?.name || 'Document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const ff = { fontFamily: "'Segoe UI', system-ui, sans-serif" };

  /* ── Loading ── */
  if (loading) return (
    <div style={{ ...ff, height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', gap: '16px' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: '36px', height: '36px', border: `4px solid #e0d5cc`, borderTop: `4px solid ${BROWN}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ color: '#888', fontSize: '14px' }}>Loading contract…</div>
    </div>
  );

  /* ── Not found ── */
  if (notFound) return (
    <div style={{ ...ff, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
      <div style={{ fontSize: '16px', color: '#666', textAlign: 'center', padding: '24px' }}>
        Contract not found or link has expired.<br/><br/>
        Questions? Call <a href="tel:6054321834" style={{ color: BROWN }}>605-432-1834</a>
      </div>
    </div>
  );

  const contract      = estimate?.contract || {};
  const alreadySigned = contract.contractStatus === 'signed';
  const custName      = contract.customer?.name || '';
  const displayImages = (stage === 'done' || alreadySigned) && signedImages.length ? signedImages : pageImages;

  /* ════ DONE ════════════════════════════════════════════════════════ */
  if (stage === 'done') return (
    <div style={{ ...ff, height: '100vh', display: 'flex', flexDirection: 'column', background: '#525659' }}>
      <div style={{ background: 'white', padding: '12px 16px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e8e8e8' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1.5px solid #86efac' }}>✓</div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#166534' }}>Contract Signed</div>
            <div style={{ fontSize: '11px', color: '#888' }}>Thank you, {custName}!</div>
          </div>
        </div>
        <button onClick={handleDownload}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: BROWN, color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>
          ⬇ Save PDF
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <PdfPages images={displayImages} />
        </div>
        <div style={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
          Questions? Call <a href="tel:6054321834" style={{ color: 'rgba(255,255,255,0.7)' }}>605-432-1834</a>
        </div>
      </div>
    </div>
  );

  /* ════ SIGN ════════════════════════════════════════════════════════ */
  if (stage === 'sign') return (
    <div style={{ ...ff, height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px 16px' }}>
        <button onClick={() => setStage('view')}
          style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '14px', padding: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          ← Back to contract
        </button>
        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', maxWidth: '540px', margin: '0 auto' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', marginBottom: '6px' }}>Sign Contract</div>
          <div style={{ fontSize: '14px', color: '#888', marginBottom: '20px', lineHeight: '1.5' }}>
            By signing below you agree to all terms of this contract and the limited warranty.
          </div>
          <div style={{ border: '2px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', background: 'white', position: 'relative', marginBottom: '8px' }}>
            <canvas ref={canvasRef}
              style={{ width: '100%', height: '160px', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
              onMouseDown={sigStart} onMouseMove={sigMove} onMouseUp={sigEnd} onMouseLeave={sigEnd}
              onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
            />
            {sigEmpty && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#ccc', fontSize: '14px', fontStyle: 'italic' }}>
                Draw your signature here
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', color: '#aaa' }}>
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            {!sigEmpty && (
              <button onClick={sigClear}
                style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Clear
              </button>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#999', lineHeight: '1.5', padding: '10px 12px', background: '#f9f9f9', borderRadius: '8px' }}>
            By clicking "Confirm & Sign" you agree to the contract terms and conditions, including the Limited Warranty. This is a legally binding agreement.
          </div>
        </div>
      </div>
      <div style={{ background: 'white', padding: '12px 16px', boxShadow: '0 -2px 12px rgba(0,0,0,0.08)', flexShrink: 0 }}>
        {submitting && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <div style={{ width: '48px', height: '48px', border: '5px solid rgba(255,255,255,0.2)', borderTop: '5px solid white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ color: 'white', fontSize: '15px', fontWeight: '600' }}>Submitting signature…</div>
          </div>
        )}
        <button onClick={handleSign} disabled={submitting || sigEmpty}
          style={{
            width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
            background: (submitting || sigEmpty) ? '#d1d5db' : TEAL,
            color: 'white', fontSize: '16px', fontWeight: '700',
            cursor: (submitting || sigEmpty) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>
          Confirm & Sign Contract
        </button>
      </div>
    </div>
  );

  /* ════ MAIN VIEW ══════════════════════════════════════════════════ */
  return (
    <div style={{ ...ff, height: '100vh', display: 'flex', flexDirection: 'column', background: '#525659' }}>

      {/* ── Header with sign button top-right ── */}
      <div style={{
        background: 'white', padding: '10px 16px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #e8e8e8',
        boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '10px', fontWeight: '800', color: BROWN, letterSpacing: '1px', textTransform: 'uppercase' }}>The Window Place LLC</div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Contract #{contract.contractNumber || '—'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {alreadySigned ? (
            <>
              <div style={{ padding: '6px 12px', background: '#f0fdf4', borderRadius: '20px', color: '#166534', fontSize: '12px', fontWeight: '600', border: '1.5px solid #86efac', whiteSpace: 'nowrap' }}>
                ✓ Signed
              </div>
              <button onClick={handleDownload}
                style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: BROWN, color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                ⬇ Save
              </button>
            </>
          ) : (
            <button onClick={() => setStage('sign')}
              style={{
                padding: '9px 20px', borderRadius: '10px', border: 'none',
                background: TEAL, color: 'white', fontSize: '14px', fontWeight: '700',
                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(26,92,82,0.3)',
              }}>
              ✍ Sign Contract
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable PDF pages (rendered as images = fits any device) ── */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <PdfPages images={displayImages} />
        </div>
        <div style={{ textAlign: 'center', padding: '16px 16px 24px', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
          The Window Place LLC · 1300 E 4th Ave, Milbank, SD 57252 · (605) 432-1834
        </div>
      </div>
    </div>
  );
}